import { encryptPayloadECB } from './crypto'

export type MonkeeConfig = Window['monkeeTranscription']

/**
 * Erro de API com code original do backend (ex: 'WARN', 'ERROR').
 * Permite que o caller distinga warnings de erros fatais.
 */
export class ApiError extends Error {
  code: string
  constructor(message: string, code: string) {
    super(message)
    this.name = 'ApiError'
    this.code = code
  }
}

export type FileItem = {
  fileId: string
  title: string
  displayName?: string | null
  type: string | null
  bytes: number
  status: string
  currentState: string | null
  currentSubState: string | null
  progress: number | null
  message: string | null
  createdAt: string | null
  completedAt: string | null
  transcriptionCompletedAt?: number | null
  analysisCompletedAt?: number | null
  // Novos campos para estado de processamento detalhado
  processingState?: 'PENDING' | 'UPLOADING' | 'TRANSCRIBING' | 'PROCESSING_TRANSCRIPTION' | 'ANALYZING' | 'COMPLETED' | 'FAILED' | 'WARNING' | null
  estimatedSecondsRemaining?: number | null
  processingStartedAt?: number | null
  estimatedCompletionAt?: number | null
  // Campo de pasta
  folderId?: string | null
  // Configurações de análise
  selectedAnalyses?: string[] | null
  // Campos de erro/aviso detalhado (disponíveis quando processingState=FAILED ou WARNING)
  errorMessage?: string | null
  errorCode?: string | null
}

export type FileListResponse = {
  files: FileItem[]
  totalFiles: number
  page: number
  pageSize: number
  totalPages: number
}

// Novo tipo para a listagem baseada em TranscriptionAnalysis
export type TranscriptionListItem = {
  fileId: string
  title: string
  displayName?: string | null
  extension: string | null
  bytes: number | null
  processingState: 'PENDING' | 'UPLOADING' | 'TRANSCRIBING' | 'PROCESSING_TRANSCRIPTION' | 'ANALYZING' | 'COMPLETED' | 'FAILED' | 'WARNING' | null
  processingSubState: string | null
  progressPercent: number
  progressMessage: string | null
  estimatedSecondsRemaining: number | null
  createdAt: number | null
  processingStartedAt: number | null
  transcriptionStartedAt: number | null
  estimatedCompletionAt: number | null
  transcriptionCompletedAt: number | null
  analysisCompletedAt: number | null
  status: string | null
  folderId: string | null
  selectedAnalyses: string[] | null
  errorMessage: string | null
  errorCode: string | null
}

export type TranscriptionListResponse = {
  files: TranscriptionListItem[]
  totalFiles: number
  page: number
  pageSize: number
  totalPages: number
}

/** Resposta do endpoint GET /api/v1/quota (monkee-file-transcript-api) */
export type UploadQuotaResponse = {
  used: number
  limit: number
  remaining: number
  quotaDate: string
  betaModeEnabled: boolean
  maxFileBytes: number
  maxConcurrentUploads: number
  maxSummaryRegenerations: number
  comparisonEnabled: boolean
}

type UploadCreateResponse = {
  fileId: string
  presignedUrl: string
  headers: Record<string, string>
}

type DownloadResponse = {
  fileId: string
  title: string
  transcriptionUrl: string
  expiresIn: number
}

type StatusResponse = {
  status: string
  currentState: string | null
  currentSubState: string | null
  progress: number | null
  message: string | null
}

type ClientConfig = {
  baseUrl: string
  aiBaseUrl?: string
  apiKey: string
  secret?: string
  userEmail?: string
  userId?: string
  tennantId?: string
  userTags?: string[]
}

const envConfig: ClientConfig | null = (() => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined
  const apiKey = import.meta.env.VITE_API_KEY as string | undefined
  if (!baseUrl || !apiKey) return null
  return {
    baseUrl,
    aiBaseUrl: import.meta.env.VITE_AI_API_BASE_URL as string | undefined,
    apiKey,
    secret: import.meta.env.VITE_API_SECRET as string | undefined,
    userEmail: import.meta.env.VITE_API_USER_EMAIL as string | undefined,
    userId: import.meta.env.VITE_API_TEST_USER as string | undefined,
    tennantId: import.meta.env.VITE_API_TEST_TENNANT as string | undefined
  }
})()

// Em desenvolvimento, popula window.monkeeTranscription a partir do .env
// Apenas se userId e tennantId de teste estiverem configurados
if (import.meta.env.DEV && envConfig && !window.monkeeTranscription && envConfig.userId && envConfig.tennantId) {
  console.log('Populando window.monkeeTranscription a partir do .env (test credentials)')
  console.log(envConfig)
  window.monkeeTranscription = {
    apiBaseUrl: envConfig.baseUrl,
    aiBaseUrl: envConfig.aiBaseUrl || 'http://localhost:8080/ai',
    apiKey: envConfig.apiKey,
    encryptedKey: envConfig.secret || '',
    userEmail: envConfig.userEmail || 'dev@local.com',
    userId: envConfig.userId ? parseInt(envConfig.userId, 10) : 0,
    tennantId: envConfig.tennantId,
    ajaxUrl: '',
    restUrl: envConfig.baseUrl,
    nonce: ''
  }
}

function resolveConfig(): ClientConfig | null {
  if (window.monkeeTranscription?.apiBaseUrl && window.monkeeTranscription.apiKey) {
    const { apiBaseUrl, aiBaseUrl, apiKey, encryptedKey, userEmail, userId, userTags } = window.monkeeTranscription
    return { 
      baseUrl: apiBaseUrl,
      aiBaseUrl: aiBaseUrl || 'http://localhost:8080/ai',
      apiKey, 
      secret: encryptedKey, 
      userEmail,
      userId: userId?.toString(),
      tennantId: window.monkeeTranscription.tennantId as string | undefined,
      userTags
    }
  }
  return envConfig
}

function buildAiUrl(path: string, cfg: ClientConfig) {
  const base = (cfg.aiBaseUrl || 'http://localhost:8080/ai').replace(/\/$/, '')
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  return `${base}${cleanPath}`
}

async function buildHeaders(cfg: ClientConfig): Promise<HeadersInit> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'x-auth-token': cfg.apiKey
  }

  // Gerar X-WordPress-API-Key criptografado
  if (cfg.userId && cfg.tennantId && cfg.secret) {
    try {
      const encryptedKey = await encryptPayloadECB(
        { userId: cfg.userId, tennantId: cfg.tennantId },
        cfg.secret
      )
      headers['X-WordPress-API-Key'] = encryptedKey
    } catch (e) {
      console.error('Erro ao gerar header criptografado:', e)
      throw new Error('Falha ao gerar autenticação')
    }
  }

  return headers
}

async function request<T>(input: RequestInfo, init: RequestInit = {}): Promise<T> {
  const res = await fetch(input, init)
  const text = await res.text()
  let data: any = null
  try {
    data = text ? JSON.parse(text) : null
  } catch (e) {
    console.error('Erro ao interpretar resposta', e, text)
  }
  
  // Verificar erros HTTP
  if (!res.ok) {
    const msg = data?.msg || data?.message || res.statusText
    const code = data?.code || 'ERROR'
    throw new ApiError(msg, code)
  }
  
  // Verificar erros de negócio retornados com 200 OK
  // O backend pode retornar { code: "WARN|ERROR|VALIDATION_ERROR", msg: "..." }
  if (data && data.code && data.code !== 'OK' && data.code !== 'SUCCESS') {
    const errorCodes = ['WARN', 'ERROR', 'VALIDATION_ERROR', 'UNAUTHORIZED', 'NOT_FOUND']
    if (errorCodes.includes(data.code)) {
      throw new ApiError(data.msg || data.message || 'Erro na operação', data.code)
    }
  }
  
  return data?.data ?? data
}

/**
 * Retorna a cota diária de uploads do player.
 * Chamado apenas no modo BETA para exibir o status de cota antes do upload.
 */
export async function getUploadQuota(): Promise<UploadQuotaResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  // Tags enviadas como query param para evitar CORS preflight com header customizado
  const tagsParam = cfg.userTags && cfg.userTags.length > 0
    ? `?tags=${encodeURIComponent(cfg.userTags.join(','))}`
    : ''
  const url = buildAiUrl(`/api/v1/quota${tagsParam}`, cfg)
  const headers = await buildHeaders(cfg)

  return request<UploadQuotaResponse>(url, { headers })
}

/**
 * Lista transcrições do usuário usando o endpoint baseado em TranscriptionAnalysis.
 * Retorna informações completas de progresso e estado de processamento.
 */
export async function listTranscriptions(params?: { page?: number; size?: number; sort?: string; folderId?: string | null; filterByFolder?: boolean }): Promise<TranscriptionListResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const { page = 0, size = 20, sort = 'createdAt,desc', folderId, filterByFolder = false } = params || {}
  let url = buildAiUrl(`/api/v1/transcription/list?page=${page}&size=${size}&sort=${encodeURIComponent(sort)}&filterByFolder=${filterByFolder}`, cfg)
  if (folderId) {
    url += `&folderId=${encodeURIComponent(folderId)}`
  }
  const headers = await buildHeaders(cfg)

  return request<TranscriptionListResponse>(url, { headers })
}

/**
 * Converte TranscriptionListItem para FileItem para manter compatibilidade.
 */
function transcriptionToFileItem(t: TranscriptionListItem): FileItem {
  // Mapear processingState para status legado
  let status = 'processing'
  if (t.processingState === 'COMPLETED') {
    status = 'completed'
  } else if (t.processingState === 'FAILED' || t.processingState === 'WARNING') {
    status = 'failed'
  }
  
  return {
    fileId: t.fileId,
    title: t.title,
    displayName: t.displayName ?? null,
    type: t.extension,
    bytes: t.bytes ?? 0,
    status,
    currentState: t.processingState ?? null,
    currentSubState: t.processingSubState,
    progress: t.progressPercent,
    message: t.progressMessage,
    createdAt: t.createdAt?.toString() ?? null,
    completedAt: t.analysisCompletedAt?.toString() ?? null,
    transcriptionCompletedAt: t.transcriptionCompletedAt ?? null,
    analysisCompletedAt: t.analysisCompletedAt ?? null,
    // Novos campos
    processingState: t.processingState,
    estimatedSecondsRemaining: t.estimatedSecondsRemaining,
    processingStartedAt: t.processingStartedAt,
    transcriptionStartedAt: t.transcriptionStartedAt ?? null,
    estimatedCompletionAt: t.estimatedCompletionAt,
    folderId: t.folderId ?? null,
    selectedAnalyses: t.selectedAnalyses ?? null,
    errorMessage: t.errorMessage ?? null,
    errorCode: t.errorCode ?? null
  }
}

/**
 * Lista arquivos usando o novo endpoint baseado em TranscriptionAnalysis.
 * Retorna no formato FileListResponse para manter compatibilidade com o UI existente.
 */
export async function listFilesV2(params?: { page?: number; size?: number; sort?: string; folderId?: string | null; filterByFolder?: boolean }): Promise<FileListResponse> {
  const data = await listTranscriptions(params)
  
  // Tratar caso onde a resposta não tem os campos esperados
  const files = data?.files || []
  
  return {
    files: files.map(transcriptionToFileItem),
    totalFiles: data?.totalFiles || 0,
    page: data?.page || 0,
    pageSize: data?.pageSize || 20,
    totalPages: data?.totalPages || 0
  }
}

/**
 * Obtém o status detalhado de um arquivo específico.
 * Use este endpoint para polling de status individual em vez de recarregar toda a lista.
 */
export async function getFileStatus(fileId: string): Promise<FileItem> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl(`/api/v1/transcription/${fileId}/status`, cfg)
  const headers = await buildHeaders(cfg)

  const transcription = await request<TranscriptionListItem>(url, { headers })
  return transcriptionToFileItem(transcription)
}

/**
 * Opções de upload e análise
 */
export type UploadOptionsApi = {
  /** ID da pasta onde o arquivo será criado (null = raiz) */
  folderId?: string | null
  /** Se true, habilita análise da transcrição */
  enableAnalysis?: boolean
  /** Lista de análises a serem executadas */
  selectedAnalyses?: string[]
  /** Se true, envia email de notificação ao concluir */
  notifyByEmail?: boolean
  /** Email para notificação */
  notificationEmail?: string
  /** Se true, retém a mídia original no S3 por 72h para reprodução */
  retainMedia?: boolean
  /** ID único do lote de upload (UUID gerado pelo frontend antes do loop de arquivos) */
  uploadBatchId?: string
  /** Total de arquivos no lote — necessário para o batch saber quando todos terminaram */
  totalFiles?: number
}

export async function createUpload(file: File, options?: UploadOptionsApi): Promise<UploadCreateResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const body = {
    title: file.name,
    bytes: file.size,
    type: getFileExtension(file.name, file.type),
    folderId: options?.folderId ?? null,
    enableAnalysis: options?.enableAnalysis ?? true,
    selectedAnalyses: options?.selectedAnalyses ?? ['sentiment', 'entities', 'topics', 'patterns', 'summary', 'timeline'],
    notifyByEmail: options?.notifyByEmail ?? false,
    notificationEmail: options?.notificationEmail ?? null,
    retainMedia: options?.retainMedia ?? false,
    uploadBatchId: options?.uploadBatchId ?? null,
    totalFiles: options?.totalFiles ?? 1,
    userTags: cfg.userTags ?? []
  }

  const url = buildAiUrl('/api/v2/wordpress/transcription/create', cfg)
  const headers = await buildHeaders(cfg)
  const response = await request<any>(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  })

  // Mapeia resposta v2 para o formato existente do frontend
  return {
    fileId: response.fileId,
    presignedUrl: response.uploadUrl,
    headers: response.signedHeaders || {}
  }
}

export async function uploadToPresigned(
  presignedUrl: string,
  headers: Record<string, string>,
  file: File,
  onProgress?: (progress: number) => void
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100
          onProgress(percentComplete)
        }
      })
    }

    // Handle completion
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`Falha ao enviar para storage: ${xhr.status}`))
      }
    })

    // Handle errors
    xhr.addEventListener('error', () => {
      reject(new Error('Falha ao enviar para storage'))
    })

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload cancelado'))
    })

    // Lista de headers "unsafe" que o browser não permite definir manualmente
    const unsafeHeaders = [
      'accept-charset',
      'accept-encoding',
      'access-control-request-headers',
      'access-control-request-method',
      'connection',
      'content-length',
      'cookie',
      'cookie2',
      'date',
      'dnt',
      'expect',
      'host',
      'keep-alive',
      'origin',
      'referer',
      'te',
      'trailer',
      'transfer-encoding',
      'upgrade',
      'via'
    ]

    // Open connection and set headers
    xhr.open('PUT', presignedUrl)
    Object.entries(headers).forEach(([key, value]) => {
      // Filtra headers unsafe (case-insensitive)
      if (!unsafeHeaders.includes(key.toLowerCase())) {
        xhr.setRequestHeader(key, value)
      }
    })
    if (!headers['Content-Type'] && !headers['content-type']) {
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
    }

    // Send file
    xhr.send(file)
  })
}

export async function confirmUpload(fileId: string) {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v2/wordpress/transcription/${fileId}/success`, cfg)
  const headers = await buildHeaders(cfg)
  return request(url, { method: 'PUT', headers })
}

/**
 * Verifica se a entidade TranscriptionAnalysis foi criada no backend
 * Retorna true se existe, false se ainda não foi criada
 */
export async function checkTranscriptionAnalysisExists(fileId: string): Promise<boolean> {
  try {
    const cfg = resolveConfig()
    if (!cfg) throw new Error('Configuração de API ausente')
    const url = buildAiUrl(`/api/v1/transcription/${fileId}/status`, cfg)
    const headers = await buildHeaders(cfg)
    await request<TranscriptionListItem>(url, { headers })
    return true // Se retornou sem erro, a entidade existe
  } catch (error) {
    // Se retornou 404 ou erro, a entidade ainda não existe
    return false
  }
}

/**
 * Atualiza o displayName (nome personalizado pelo usuário) de uma transcrição.
 * Se displayName for null ou vazio, reverte para o título original do arquivo.
 */
export async function updateDisplayName(fileId: string, displayName: string | null): Promise<{ fileId: string; displayName: string }> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/displayName`, cfg)
  const headers = await buildHeaders(cfg)
  return request(url, { 
    method: 'PUT', 
    headers,
    body: JSON.stringify({ displayName: displayName || '' })
  })
}

/**
 * Atualiza o mapeamento de speakers (nomes personalizados)
 */
export async function updateSpeakerMapping(fileId: string, speakerMapping: Record<string, string>): Promise<{ status: string; message: string; speakerMapping: Record<string, string> }> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/speaker`, cfg)
  const headers = await buildHeaders(cfg)
  return request(url, { 
    method: 'PUT', 
    headers,
    body: JSON.stringify(speakerMapping)
  })
}

/**
 * Salva edições de segmentos da transcrição.
 * Sobrescreve o JSON no S3 e cria registros de auditoria no MongoDB.
 */
export async function saveSegmentEdits(fileId: string, edits: Array<{ segmentIndex: number; text?: string; speaker?: string }>): Promise<{ status: string; message: string; editedCount: number }> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/segments`, cfg)
  const headers = await buildHeaders(cfg)
  return request(url, {
    method: 'PUT',
    headers,
    body: JSON.stringify(edits)
  })
}

export async function failUpload(fileId: string) {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v2/wordpress/transcription/${fileId}/fail`, cfg)
  const headers = await buildHeaders(cfg)
  return request(url, { method: 'PUT', headers })
}

export async function getStatus(fileId: string): Promise<StatusResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v2/wordpress/transcription/${fileId}/status`, cfg)
  const headers = await buildHeaders(cfg)
  return request<StatusResponse>(url, { headers })
}

export async function deleteFile(fileId: string) {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v2/wordpress/transcription/${fileId}/delete`, cfg)
  const headers = await buildHeaders(cfg)
  return request(url, { method: 'DELETE', headers })
}

export async function downloadFile(fileId: string): Promise<DownloadResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v2/wordpress/transcription/${fileId}/result`, cfg)
  const headers = await buildHeaders(cfg)
  const response = await request<any>(url, { headers })

  // Mapeia resposta v2 para o formato existente
  return {
    fileId,
    title: '',
    transcriptionUrl: response.downloadUrl,
    expiresIn: 900 // 15 minutos (presigned URL padrão)
  }
}

// ===============================================
// MEDIA PLAYBACK - PRESIGNED URL PARA ÁUDIO/VÍDEO
// ===============================================

export type MediaUrlResponse = {
  mediaUrl: string | null
  mediaAvailable: boolean
  contentType: string | null
  expiresIn: number
  message: string | null
}

/**
 * Obtém URL presigned para streaming do arquivo de mídia original.
 * A mídia é retida temporariamente após a transcrição (TTL configurável, padrão 72h).
 * Retorna mediaAvailable=false quando a mídia já expirou ou foi removida.
 */
export async function getMediaUrl(fileId: string): Promise<MediaUrlResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v2/wordpress/transcription/${fileId}/media-url`, cfg)
  const headers = await buildHeaders(cfg)
  return request<MediaUrlResponse>(url, { headers })
}

// ===============================================
// ANÁLISE DE TRANSCRIÇÕES - NOVOS ENDPOINTS
// ===============================================

import type { 
  Transcription, 
  TranscriptionMetrics,
  SentimentAnalysis,
  EntityExtraction,
  TopicDetection,
  EventTimeline,
  SpeechPatternAnalysis,
  AISummary,
  MultiTranscriptionComparison,
  UserAnnotations
} from '../types/transcription'
import { isAnalysisNotAvailable } from '../types/transcription'

export type TranscriptionResponse = {
  transcription: Transcription
  metrics: TranscriptionMetrics
}

/**
 * Status de cada tipo de análise
 * Valores possíveis: PENDING, PROCESSING, COMPLETED, FAILED, SKIPPED
 */
export type AnalysisStatus = {
  overall: string
  sentiment: string
  entities: string
  topics: string
  patterns: string
  summary: string
  timeline: string
  analysisTime: number
  errorMessage: string | null
  selectedAnalyses: string[] | null
  analysisOutdated: boolean
}

/**
 * Obtém o status atual de cada análise para polling
 * Frontend deve chamar este endpoint periodicamente para atualizar a UI
 */
export async function getAnalysisStatus(fileId: string): Promise<AnalysisStatus> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/analysis-status`, cfg)
  const headers = await buildHeaders(cfg)
  return request<AnalysisStatus>(url, { headers })
}

/**
 * Executa uma análise específica sob demanda
 * Usado quando o usuário não selecionou a análise no upload e quer executar depois
 * 
 * @param fileId - ID do arquivo
 * @param analysisType - Tipo da análise: sentiment, entities, topics, patterns, summary, timeline
 * @returns Status atualizado de todas as análises
 */
export async function runOnDemandAnalysis(fileId: string, analysisType: string): Promise<AnalysisStatus> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/analysis/${analysisType}/run`, cfg)
  const headers = await buildHeaders(cfg)
  return request<AnalysisStatus>(url, { method: 'POST', headers })
}

// ===============================================
// RETRY DE TRANSCRIÇÃO
// ===============================================

/**
 * Tipo de retry
 * - file: Refaz toda a transcrição desde o início
 * - transcription: Refaz apenas a análise da transcrição existente
 */
export type RetryType = 'file' | 'transcription'

/**
 * Resposta do retry
 */
export type RetryResponse = {
  fileId: string
  status: string
  processingState: string
  message: string
  retryCount: number
}

/**
 * Solicita retry de transcrição ou análise
 * 
 * @param fileId ID do arquivo
 * @param type Tipo de retry:
 *   - 'file': Refaz toda a transcrição (upload → transcrição → análise)
 *   - 'transcription': Refaz apenas a análise da transcrição existente
 * @returns RetryResponse com status atualizado
 */
export async function retryTranscription(fileId: string, type: RetryType = 'transcription'): Promise<RetryResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v2/wordpress/transcription/${fileId}/retry?type=${type}`, cfg)
  const headers = await buildHeaders(cfg)
  return request<RetryResponse>(url, { method: 'POST', headers })
}

/**
 * Obtém transcrição completa com métricas
 */
export async function getTranscription(fileId: string): Promise<TranscriptionResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}`, cfg)
  const headers = await buildHeaders(cfg)
  return request<TranscriptionResponse>(url, { headers })
}

/**
 * Obtém análise de sentimento
 * @returns null se a análise ainda não está disponível (arquivo em processamento)
 */
export async function getSentimentAnalysis(fileId: string): Promise<SentimentAnalysis | null> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/sentiment`, cfg)
  const headers = await buildHeaders(cfg)
  const result = await request<SentimentAnalysis>(url, { headers })
  if (isAnalysisNotAvailable(result)) return null
  return result
}

/**
 * Obtém extração de entidades
 * @returns null se a análise ainda não está disponível (arquivo em processamento)
 */
export async function getEntityExtraction(fileId: string): Promise<EntityExtraction | null> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/entities`, cfg)
  const headers = await buildHeaders(cfg)
  const result = await request<EntityExtraction>(url, { headers })
  if (isAnalysisNotAvailable(result)) return null
  return result
}

/**
 * Obtém detecção de tópicos
 * @returns null se a análise ainda não está disponível (arquivo em processamento)
 */
export async function getTopicDetection(fileId: string): Promise<TopicDetection | null> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/topics`, cfg)
  const headers = await buildHeaders(cfg)
  const result = await request<TopicDetection>(url, { headers })
  if (isAnalysisNotAvailable(result)) return null
  return result
}

/**
 * Obtém timeline de eventos
 * @returns null se a análise ainda não está disponível (arquivo em processamento)
 */
export async function getEventTimeline(fileId: string): Promise<EventTimeline | null> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/timeline`, cfg)
  const headers = await buildHeaders(cfg)
  const result = await request<EventTimeline>(url, { headers })
  if (isAnalysisNotAvailable(result)) return null
  return result
}

/**
 * Obtém análise de padrões de fala
 * @returns null se a análise ainda não está disponível (arquivo em processamento)
 */
export async function getSpeechPatternAnalysis(fileId: string): Promise<SpeechPatternAnalysis | null> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/patterns`, cfg)
  const headers = await buildHeaders(cfg)
  const result = await request<SpeechPatternAnalysis>(url, { headers })
  if (isAnalysisNotAvailable(result)) return null
  return result
}

/**
 * Obtém resumo gerado por IA
 * @returns null se a análise ainda não está disponível (arquivo em processamento)
 */
export async function getAISummary(fileId: string): Promise<AISummary | null> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/summary`, cfg)
  const headers = await buildHeaders(cfg)
  const result = await request<AISummary>(url, { headers })
  if (isAnalysisNotAvailable(result)) return null
  return result
}

/**
 * Opções de customização do resumo para advogados criminalistas
 */
export type SummaryCustomization = {
  /** Foco do resumo: geral, fatos_evidencias, argumentacao, depoimentos, cronologia */
  focus?: string
  /** Nível de detalhe: resumido, moderado, detalhado */
  detailLevel?: string
  /** Perspectiva: neutro, defesa, acusacao */
  perspective?: string
  /** Elementos a destacar */
  highlights?: string[]
  /** Formato de saída: corrido, topicos, peticao */
  outputFormat?: string
}

/**
 * Regenera o resumo com IA, opcionalmente com opções de customização
 * @param fileId ID do arquivo
 * @param customization Opções de customização (opcional)
 * @returns Novo resumo gerado
 */
export async function regenerateAISummary(fileId: string, customization?: SummaryCustomization): Promise<AISummary | null> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/summary/regenerate`, cfg)
  const headers = await buildHeaders(cfg)
  const result = await request<AISummary>(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(customization ?? {})
  })
  if (isAnalysisNotAvailable(result)) return null
  return result
}

/**
 * Obtém anotações do usuário para uma transcrição
 */
export async function getAnnotations(fileId: string): Promise<UserAnnotations> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/annotations`, cfg)
  const headers = await buildHeaders(cfg)
  try {
    const result = await request<UserAnnotations>(url, { headers })
    return result
  } catch {
    // Retorna estrutura vazia se não existir
    return { items: [], highlights: [], bookmarks: [] }
  }
}

/**
 * Atualiza anotações do usuário para uma transcrição
 */
export async function updateAnnotations(fileId: string, annotations: UserAnnotations): Promise<UserAnnotations> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl(`/api/v1/transcription/${fileId}/annotations`, cfg)
  const headers = await buildHeaders(cfg)
  return request<UserAnnotations>(url, {
    method: 'PUT',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(annotations)
  })
}

/**
 * Compara múltiplas transcrições
 */
export async function compareTranscriptions(fileIds: string[]): Promise<MultiTranscriptionComparison> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl('/api/v1/transcription/compare', cfg)
  const headers = await buildHeaders(cfg)
  return request<MultiTranscriptionComparison>(url, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileIds })
  })
}

/**
 * Tipos de arquivo suportados pelo backend (enum MediaFileType)
 */
const SUPPORTED_EXTENSIONS = ['MP3', 'WAV', 'M4A', 'OGG', 'MP4'] as const
type SupportedExtension = typeof SUPPORTED_EXTENSIONS[number]

/**
 * Extrai a extensão do arquivo e retorna em maiúsculo.
 * Se não for uma extensão suportada, tenta inferir pelo MIME type.
 */
function getFileExtension(fileName: string, mimeType?: string): SupportedExtension {
  // Extrai extensão do nome do arquivo
  const match = fileName.match(/\.([a-zA-Z0-9]+)$/)
  if (match) {
    const ext = match[1].toUpperCase()
    if (SUPPORTED_EXTENSIONS.includes(ext as SupportedExtension)) {
      return ext as SupportedExtension
    }
  }

  // Fallback: tenta inferir pelo MIME type
  if (mimeType) {
    const mimeToExt: Record<string, SupportedExtension> = {
      'audio/mpeg': 'MP3',
      'audio/mp3': 'MP3',
      'audio/wav': 'WAV',
      'audio/wave': 'WAV',
      'audio/x-wav': 'WAV',
      'audio/m4a': 'M4A',
      'audio/x-m4a': 'M4A',
      'audio/mp4': 'M4A',
      'audio/ogg': 'OGG',
      'video/mp4': 'MP4',
      'video/mpeg': 'MP4'
    }
    const extFromMime = mimeToExt[mimeType.toLowerCase()]
    if (extFromMime) return extFromMime
  }

  // Default para áudio genérico
  return 'MP3'
}

// ===============================================
// PASTAS - API DE GERENCIAMENTO DE PASTAS
// ===============================================

import type {
  TranscriptionFolder,
  FolderListResponse,
  BreadcrumbResponse,
  CreateFolderRequest,
  UpdateFolderRequest,
  MoveFileRequest,
  MoveFilesRequest
} from '../types/folder'

export type { TranscriptionFolder, FolderListResponse, BreadcrumbResponse }

/**
 * Lista pastas do usuário. Se parentFolderId é fornecido, lista subpastas.
 */
export async function listFolders(parentFolderId?: string | null): Promise<TranscriptionFolder[]> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  let url = buildAiUrl('/api/v1/transcription/folders', cfg)
  if (parentFolderId) {
    url += `?parentFolderId=${encodeURIComponent(parentFolderId)}`
  }
  const headers = await buildHeaders(cfg)

  const response = await request<FolderListResponse>(url, { headers })
  return response?.folders || []
}

/**
 * Lista todas as pastas do usuário (para árvore de seleção)
 */
export async function listAllFolders(): Promise<TranscriptionFolder[]> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl('/api/v1/transcription/folders/all', cfg)
  const headers = await buildHeaders(cfg)

  const response = await request<FolderListResponse>(url, { headers })
  return response?.folders || []
}

/**
 * Cria uma nova pasta
 */
export async function createFolder(data: CreateFolderRequest): Promise<TranscriptionFolder> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl('/api/v1/transcription/folders', cfg)
  const headers = await buildHeaders(cfg)

  return request<TranscriptionFolder>(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(data)
  })
}

/**
 * Atualiza uma pasta (nome, cor, ícone)
 */
export async function updateFolder(folderId: string, data: UpdateFolderRequest): Promise<TranscriptionFolder> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl(`/api/v1/transcription/folders/${folderId}`, cfg)
  const headers = await buildHeaders(cfg)

  return request<TranscriptionFolder>(url, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data)
  })
}

/**
 * Move uma pasta para dentro de outra (ou para a raiz se targetFolderId=null)
 */
export async function moveFolder(folderId: string, targetFolderId: string | null): Promise<TranscriptionFolder> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl(`/api/v1/transcription/folders/${folderId}/move`, cfg)
  const headers = await buildHeaders(cfg)

  return request<TranscriptionFolder>(url, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ targetFolderId })
  })
}

/**
 * Deleta uma pasta (move arquivos para a raiz)
 */
export async function deleteFolder(folderId: string): Promise<void> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl(`/api/v1/transcription/folders/${folderId}`, cfg)
  const headers = await buildHeaders(cfg)

  await request(url, { method: 'DELETE', headers })
}

/**
 * Obtém o breadcrumb de uma pasta
 */
export async function getFolderBreadcrumb(folderId: string): Promise<TranscriptionFolder[]> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl(`/api/v1/transcription/folders/${folderId}/breadcrumb`, cfg)
  const headers = await buildHeaders(cfg)

  const response = await request<BreadcrumbResponse>(url, { headers })
  return response?.path || []
}

/**
 * Move um arquivo para uma pasta (ou raiz se folderId=null)
 */
export async function moveFileToFolder(fileId: string, folderId: string | null): Promise<void> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl(`/api/v1/transcription/${fileId}/move`, cfg)
  const headers = await buildHeaders(cfg)

  await request(url, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ folderId } as MoveFileRequest)
  })
}

/**
 * Move múltiplos arquivos para uma pasta
 */
export async function moveFilesToFolder(fileIds: string[], folderId: string | null): Promise<{ moved: number; total: number }> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl('/api/v1/transcription/move-files', cfg)
  const headers = await buildHeaders(cfg)

  return request<{ moved: number; total: number }>(url, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ fileIds, folderId } as MoveFilesRequest)
  })
}

// ===============================================
// BUSCA AVANÇADA - API DE BUSCA
// ===============================================

/**
 * Escopo da busca - onde procurar
 */
export interface ApiSearchScope {
  titles?: boolean
  entities?: boolean
  summaries?: boolean
  annotations?: boolean
  timeline?: boolean
  topics?: boolean
}

/**
 * Filtros de busca
 */
export interface ApiSearchFilters {
  speakers?: string[]
  sentiment?: string[]
  entityTypes?: string[]
  processingState?: string
  folderId?: string
  hasAnnotations?: boolean
  fileTitle?: string
}

/**
 * Requisição de busca
 */
export interface ApiSearchRequest {
  query: string
  scope?: ApiSearchScope
  filters?: ApiSearchFilters
  page?: number
  pageSize?: number
  sort?: string
}

/**
 * Match de arquivo nos resultados
 */
export interface ApiFileMatch {
  id: string
  fileId: string
  title: string
  folderId: string | null
  createdAt: number | null
  processingState: string | null
  progressPercent: number | null
  matchCount: number
  preview: string
  matchedIn: string[]
}

/**
 * Match de entidade
 */
export interface ApiEntityMatch {
  fileId: string
  fileName: string
  entityType: string
  entityValue: string
  normalizedValue: string | null
  occurrenceCount: number
  relevance: number
  highlightedContext: string
}

/**
 * Match em resumo
 */
export interface ApiSummaryMatch {
  fileId: string
  fileName: string
  matchType: string
  text: string
  highlightedText: string
}

/**
 * Match em anotação
 */
export interface ApiAnnotationMatch {
  fileId: string
  fileName: string
  annotationType: string
  annotationId: string
  text: string
  highlightedText: string
  segmentIndex: number
  color: string | null
  category: string | null
  createdAt: number | null
}

/**
 * Match em timeline
 */
export interface ApiTimelineMatch {
  fileId: string
  fileName: string
  eventId: string
  title: string
  description: string
  highlightedDescription: string
  category: string
  importance: string
  timestamp: string
  participants: string[]
}

/**
 * Resultados da busca
 */
export interface ApiSearchResults {
  files: ApiFileMatch[]
  entities: ApiEntityMatch[]
  summaries: ApiSummaryMatch[]
  annotations: ApiAnnotationMatch[]
  timeline: ApiTimelineMatch[]
}

/**
 * Pessoa mencionada
 */
export interface ApiPersonMention {
  name: string
  count: number
  fileIds: string[]
}

/**
 * Local mencionado
 */
export interface ApiLocationMention {
  name: string
  count: number
  fileIds: string[]
}

/**
 * Distribuição de sentimento
 */
export interface ApiSentimentDistribution {
  positive: number
  neutral: number
  negative: number
}

/**
 * Insights da busca
 */
export interface ApiSearchInsights {
  topPeople: ApiPersonMention[]
  topLocations: ApiLocationMention[]
  sentimentDistribution: ApiSentimentDistribution
  topFileIds: string[]
}

/**
 * Resposta completa da busca
 */
export interface ApiSearchResponse {
  query: string
  totalResults: number
  queryTime: number
  page: number
  pageSize: number
  totalPages: number
  results: ApiSearchResults
  insights: ApiSearchInsights
}

/**
 * Sugestão de autocomplete
 */
export interface ApiAutocompleteSuggestion {
  text: string
  type: 'speaker' | 'entity' | 'file' | 'term' | 'topic'
  entityType: string | null
  count: number
  relevance: number
}

/**
 * Resposta do autocomplete
 */
export interface ApiAutocompleteResponse {
  suggestions: ApiAutocompleteSuggestion[]
  queryTime: number
}

/**
 * Opção de pasta para filtros
 */
export interface ApiFolderOption {
  id: string
  name: string
  color: string | null
}

/**
 * Opções de filtros disponíveis
 */
export interface ApiSearchFiltersOptions {
  folders: ApiFolderOption[]
  entityTypes: string[]
  speakers: string[]
  processingStates: string[]
  sentiments: string[]
}

/**
 * Executa busca avançada em transcrições
 */
export async function searchTranscriptions(searchRequest: ApiSearchRequest): Promise<ApiSearchResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl('/api/v1/transcription/search', cfg)
  const headers = await buildHeaders(cfg)

  return request<ApiSearchResponse>(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(searchRequest)
  })
}

/**
 * Obtém sugestões de autocomplete
 */
export async function getAutocomplete(query: string, limit: number = 10): Promise<ApiAutocompleteResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl(`/api/v1/transcription/search/autocomplete?query=${encodeURIComponent(query)}&limit=${limit}`, cfg)
  const headers = await buildHeaders(cfg)

  return request<ApiAutocompleteResponse>(url, { headers })
}

/**
 * Obtém opções de filtros disponíveis para o usuário
 */
export async function getSearchFiltersOptions(): Promise<ApiSearchFiltersOptions> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl('/api/v1/transcription/search/filters', cfg)
  const headers = await buildHeaders(cfg)

  return request<ApiSearchFiltersOptions>(url, { headers })
}

// ===============================================
// NPS + FEEDBACK - API ENDPOINTS
// ===============================================

export type NpsCanSurveyResponse = {
  canSurvey: boolean
}

export type NpsSubmitRequest = {
  score: number
  feedback?: string
  triggerEvent: string
  fileId?: string
  currentView?: string
}

export type FeedbackSubmitRequest = {
  type: string
  message: string
  currentView?: string
}

/**
 * Verifica se o usuário pode receber a pesquisa NPS agora (cooldown + flag).
 */
export async function checkNpsSurvey(): Promise<NpsCanSurveyResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl('/api/v1/nps/can-survey', cfg)
  const headers = await buildHeaders(cfg)
  return request<NpsCanSurveyResponse>(url, { headers })
}

/**
 * Registra uma resposta NPS do usuário.
 */
export async function submitNpsResponse(data: NpsSubmitRequest): Promise<void> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl('/api/v1/nps/response', cfg)
  const headers = await buildHeaders(cfg)
  await request(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(data)
  })
}

/**
 * Registra um feedback proativo enviado pelo usuário via botão "Sugestões".
 */
export async function submitFeedback(data: FeedbackSubmitRequest): Promise<void> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')
  const url = buildAiUrl('/api/v1/nps/feedback', cfg)
  const headers = await buildHeaders(cfg)
  await request(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(data)
  })
}

/**
 * Envia email de teste (apenas em desenvolvimento).
 * Cria um lote fictício com dados de exemplo e dispara um email real via SendGrid.
 * O endpoint de backend NÃO verifica a flag notification.email.enabled.
 *
 * @param email Email de destino do teste
 */
export async function sendTestEmail(email: string): Promise<{ sent: boolean; email: string; message: string }> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl(`/api/v1/transcription-settings/email/test?email=${encodeURIComponent(email)}`, cfg)
  const headers = await buildHeaders(cfg)

  const response = await fetch(url, { method: 'POST', headers })
  if (!response.ok) {
    const text = await response.text()
    throw new Error(`Erro ${response.status}: ${text}`)
  }
  return response.json()
}

// ---------------------------------------------------------------
// Preferências de upload do usuário
// ---------------------------------------------------------------

export type UploadPreferences = {
  notifyByEmail?: boolean | null
  notificationEmail?: string | null
  retainMedia?: boolean | null
}

/**
 * Busca as preferências de upload persistidas do usuário.
 * Retorna null nos campos se o usuário nunca salvou preferências.
 */
export async function getUserUploadPreferences(): Promise<UploadPreferences> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl('/api/v2/wordpress/transcription/preferences', cfg)
  const headers = await buildHeaders(cfg)

  return request<UploadPreferences>(url, { headers })
}

/**
 * Salva as preferências de upload do usuário no banco de dados.
 */
export async function saveUserUploadPreferences(prefs: UploadPreferences): Promise<UploadPreferences> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl('/api/v2/wordpress/transcription/preferences', cfg)
  const headers = await buildHeaders(cfg)

  return request<UploadPreferences>(url, {
    method: 'PUT',
    headers,
    body: JSON.stringify(prefs)
  })
}

// ---------------------------------------------------------------
// Termos customizados do usuário
// ---------------------------------------------------------------

import type { TermScope, CustomTermConfig, CustomTermsResponse } from '../types/settings'

/**
 * Busca os termos de um escopo específico.
 */
export async function getCustomTerms(scope: TermScope = 'GLOBAL', scopeId?: string): Promise<CustomTermsResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const params = new URLSearchParams({ scope })
  if (scopeId) params.set('scopeId', scopeId)

  const url = buildAiUrl(`/api/v2/wordpress/transcription/custom-terms?${params}`, cfg)
  const headers = await buildHeaders(cfg)

  return request<CustomTermsResponse>(url, { headers })
}

/**
 * Salva os termos de um escopo específico.
 */
export async function saveCustomTerms(
  scope: TermScope,
  scopeId: string | null,
  terms: CustomTermConfig[]
): Promise<CustomTermsResponse> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl('/api/v2/wordpress/transcription/custom-terms', cfg)
  const headers = await buildHeaders(cfg)

  return request<CustomTermsResponse>(url, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ scope, scopeId, terms })
  })
}

/**
 * Remove os termos de um escopo específico.
 */
export async function deleteCustomTerms(scope: TermScope, scopeId?: string): Promise<void> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const params = new URLSearchParams({ scope })
  if (scopeId) params.set('scopeId', scopeId)

  const url = buildAiUrl(`/api/v2/wordpress/transcription/custom-terms?${params}`, cfg)
  const headers = await buildHeaders(cfg)

  await request<string>(url, { method: 'DELETE', headers })
}

/**
 * Busca os termos mesclados (GLOBAL + FOLDER chain + FILE).
 * Termos mais específicos prevalecem sobre genéricos.
 */
export async function getMergedCustomTerms(
  folderId?: string | null,
  fileId?: string | null
): Promise<CustomTermConfig[]> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const params = new URLSearchParams()
  if (folderId) params.set('folderId', folderId)
  if (fileId) params.set('fileId', fileId)

  const qs = params.toString()
  const url = buildAiUrl(`/api/v2/wordpress/transcription/custom-terms/merged${qs ? '?' + qs : ''}`, cfg)
  const headers = await buildHeaders(cfg)

  return request<CustomTermConfig[]>(url, { headers })
}

/**
 * Obtém sugestões de termos a partir da análise de um arquivo.
 */
export async function getTermSuggestions(fileId: string): Promise<CustomTermConfig[]> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl(`/api/v2/wordpress/transcription/custom-terms/suggestions/${fileId}`, cfg)
  const headers = await buildHeaders(cfg)

  return request<CustomTermConfig[]>(url, { headers })
}

/**
 * Lista todos os documentos de termos do usuário.
 */
export async function getAllCustomTerms(): Promise<CustomTermsResponse[]> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl('/api/v2/wordpress/transcription/custom-terms/all', cfg)
  const headers = await buildHeaders(cfg)

  return request<CustomTermsResponse[]>(url, { headers })
}

// ===============================================
// AI Chat
// ===============================================

export interface ChatMessageItem {
  role: 'user' | 'assistant'
  content: string
}

export interface ChatResponseData {
  message: string
  promptTokens: number
  completionTokens: number
  totalTokens: number
}

/**
 * Envia mensagem para o chat IA sobre uma transcrição.
 */
export async function chatWithTranscription(
  fileId: string,
  message: string,
  history: ChatMessageItem[]
): Promise<ChatResponseData> {
  const cfg = resolveConfig()
  if (!cfg) throw new Error('Configuração de API ausente')

  const url = buildAiUrl(`/api/v1/transcription/${fileId}/chat`, cfg)
  const headers = await buildHeaders(cfg)

  return request<ChatResponseData>(url, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, history }),
  })
}
